<script setup>
import { computed, ref } from 'vue'
import UmisisModal from '../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../resources/js/components/ui/field/index.js'
import { useChecklistsStore } from '../../stores/checklistsStore.js'
import { buildChecklistEditFormState } from '../../utils/checklistModalState.js'

const props = defineProps({
  checklistableId: {
    type: [Number, String],
    required: true,
  },
  checklistableType: {
    type: String,
    required: true,
  },
})

const { componentRef: dialog, openModal: openDialog, closeModal: closeDialog } = useModalControl()
const checklistsStore = useChecklistsStore(`checklists_card_${String(props.checklistableType).replace(/[^A-Za-z0-9]/g, '_')}_${props.checklistableId}`)

const checklist = ref(null)
const name = ref('')
const description = ref('')
const errorMessage = ref(null)
const isSaving = ref(false)
const fieldControlBaseClass = 'flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'

const canSubmit = computed(() => {
  return !isSaving.value && name.value.trim().length > 0
})

const resetModal = () => {
  checklist.value = null
  name.value = ''
  description.value = ''
  errorMessage.value = null
}

const openModal = (nextChecklist) => {
  if (!nextChecklist) {
    return
  }

  const formState = buildChecklistEditFormState(nextChecklist)

  checklist.value = nextChecklist
  name.value = formState.name.length > 0 ? formState.name : 'Checklist'
  description.value = formState.description
  errorMessage.value = null
  openDialog()
}

const closeModal = () => {
  closeDialog()
}

const handleOk = async (event) => {
  event?.preventDefault?.()

  if (!checklist.value || !canSubmit.value) {
    if (name.value.trim().length === 0) {
      errorMessage.value = 'A checklist name is required.'
    }

    return
  }

  errorMessage.value = null
  isSaving.value = true

  try {
    await checklistsStore.itemPatch(checklist.value.id, {
      name: name.value.trim(),
      description: description.value.trim().length > 0 ? description.value.trim() : null,
      checklistable_id: Number(props.checklistableId),
      checklistable_type: props.checklistableType,
    })
    await checklistsStore.fetchItems({
      clear: true,
      not_paginated: true,
      checklistable_id: Number(props.checklistableId),
      checklistable_type: props.checklistableType,
      order_by: 'started_at',
      order_direction: 'desc',
    })
    closeDialog()
  } catch (error) {
    errorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to update checklist.'
  } finally {
    isSaving.value = false
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>

<template>
  <UmisisModal
    ref="dialog"
    title="Edit Checklist"
    description="Update checklist name and description."
    ok-title="Save"
    cancel-title="Cancel"
    :ok-disabled="!canSubmit"
    @ok="handleOk"
    @hide="resetModal"
  >
    <div class="flex flex-col gap-3">
      <Field>
        <FieldLabel for="checklists-card-edit-name">
          Name
        </FieldLabel>
        <input
          id="checklists-card-edit-name"
          v-model="name"
          type="text"
          class="h-9 rounded-md border bg-background px-3 text-sm outline-none transition focus-visible:ring-1 focus-visible:ring-ring"
          :disabled="isSaving"
          placeholder="Checklist name"
        >
      </Field>

      <Field>
        <FieldLabel for="checklists-card-edit-description">
          Description
        </FieldLabel>
        <textarea
          id="checklists-card-edit-description"
          v-model="description"
          rows="4"
          :class="fieldControlBaseClass"
          placeholder="Describe this checklist..."
        />
      </Field>

      <FieldDescription v-if="errorMessage" class="text-destructive font-medium">
        {{ errorMessage }}
      </FieldDescription>
    </div>
  </UmisisModal>
</template>
